import { Alert, Text } from 'components';
import { Api } from 'lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from 'screens/Admin/components';
import CreateGamePanel from './components/CreateGamePanel';
import GameDayFeatures from './components/GameDayFeatures';
import GameControls from './components/GameControls';
import GameList from './components/GameList';
import { unwrap } from './labState';
import { Btn, Card, Empty } from './ui';

const REFRESH_MS = 5000;

const failed = (res) => !res || res.err || res.status >= 400;

async function loadFanwave(teamId) {
  const [status, list] = await Promise.all([
    Api.get(`/fanwave/${teamId}/status`, null, { silent: true }),
    Api.get(`/admin/fanwave/list?teamId=${teamId}&limit=20`, null, { silent: true })
  ]);
  return {
    status: failed(status) ? null : unwrap(status),
    waves: failed(list) ? [] : unwrap(list)?.waves || []
  };
}

// Mock games for testing game day end to end: create one, move it through the match, and watch
// every feature that hangs off it — Team Channel, polls, quizzes, POTM, predictions, FanWave and
// the GloryBoard window — on both sides at once. The backend's mock driver plays the match; this
// page only tells it where to be.
const GameDayLab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('game');
  const [games, setGames] = useState([]);
  const [serverNow, setServerNow] = useState(null);
  const [loadingGames, setLoadingGames] = useState(true);
  const [state, setState] = useState(null);
  const [fanwave, setFanwave] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // The backend's refusal, when this server is not development, staging or UAT (its APP_ENV).
  const [refused, setRefused] = useState(null);
  const refreshing = useRef(false);
  const refreshAgain = useRef(false);
  // Read at the moment a load runs, so a load queued behind another still fetches the game that
  // is selected by then rather than the one selected when it was asked for.
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  const select = useCallback(
    (id) => setSearchParams(id ? { game: id } : {}, { replace: true }),
    [setSearchParams]
  );

  const load = useCallback(async () => {
    const id = selectedRef.current;
    const list = await Api.get('/admin/mock/listMockGames', null, { silent: true });
    setRefused(list?.status === 403 ? list.err || 'Mock games are not available here.' : null);
    const nextGames = failed(list) ? [] : list.games || [];
    const game = nextGames.find((g) => g._id === id);
    const [gameState, home, away] = game
      ? await Promise.all([
          Api.get(`/admin/mock/getMockGameState?gameId=${game._id}`, null, { silent: true }),
          loadFanwave(game.home),
          loadFanwave(game.away)
        ])
      : [null, null, null];
    // The selection moved while this was loading: what came back belongs to another game.
    if (id !== selectedRef.current) return false;
    setGames(nextGames);
    setServerNow(list?.serverNow || null);
    setLoadingGames(false);
    setState(failed(gameState) ? null : gameState);
    setFanwave(game ? { home, away } : {});
    return true;
  }, []);

  /** Loads now, or once more after the load already running — never drops a request. */
  const refresh = useCallback(async () => {
    if (refreshing.current) {
      refreshAgain.current = true;
      return;
    }
    refreshing.current = true;
    do {
      refreshAgain.current = false;
      if (!(await load())) refreshAgain.current = true;
    } while (refreshAgain.current);
    refreshing.current = false;
  }, [load]);

  useEffect(() => {
    setState(null);
    setFanwave({});
    setNotice(null);
    refresh();
  }, [selectedId, refresh]);

  // Open on the newest game rather than an empty panel. Only when nothing is selected: a game
  // just created is selected before the list that contains it has loaded.
  useEffect(() => {
    if (!loadingGames && games.length && !selectedId) select(games[0]._id);
  }, [games, loadingGames, selectedId, select]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, refresh]);

  /** Runs one change, says how it went, and reloads. `fn` resolves to the API's response. */
  const act = useCallback(
    async (label, fn) => {
      setBusy(true);
      const res = await fn();
      setBusy(false);
      if (failed(res)) {
        setNotice({ tone: 'error', text: `${label}: ${res?.err || 'that did not work.'}` });
      } else {
        setNotice({ tone: 'ok', text: `${label}: done.` });
      }
      await refresh();
      return res;
    },
    [refresh]
  );

  const update = async (body, label) => {
    const res = await act(label, () =>
      Api.post('/admin/mock/updateMockGame', { gameId: selectedId, ...body }, null, {
        silent: true
      })
    );
    if (failed(res)) return;
    if (res.skipped?.includes('half_time')) {
      setNotice({
        tone: 'warn',
        text:
          `${label}: done, but half-time was skipped, so nothing that happens at half-time will ` +
          'happen for this game — HT prediction points, the GloryBoard window, the half-time ' +
          'quiz countdown. Jump to Half-time first to cover them.'
      });
    } else if (body.phase) {
      setNotice({
        tone: 'ok',
        text: `${label}: the match driver moves the game within 30 seconds.`
      });
    }
  };

  const askDelete = (gameId) =>
    setConfirm({
      text: gameId
        ? 'Delete this mock game? Its lineups, predictions, POTM poll, game-day polls, half-time quizzes and GloryBoard windows go with it.'
        : 'Delete every mock game on this environment, with everything made for them?',
      accept: async () => {
        setConfirm(null);
        const res = await act(gameId ? 'Delete game' : 'Delete all mock games', () =>
          Api.delete(
            `/admin/mock/deleteMockGames${gameId ? `?gameId=${gameId}` : ''}`,
            null,
            null,
            { silent: true, raw: true }
          )
        );
        if (!failed(res)) select(null);
      }
    });

  const selected = games.find((g) => g._id === selectedId) || null;
  const game = state?.game || selected;

  return (
    <div className="w-full">
      <Header>
        <div className="flex items-center justify-between w-full">
          <Text h3>Game Day Lab</Text>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[13px] text-[#334155]">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Refresh every 5 s
            </label>
            <Btn onClick={refresh}>Refresh</Btn>
          </div>
        </div>
      </Header>

      <div className="w-full mt-[54px] bg-[#F8FAFC] min-h-[calc(100vh-54px)] p-4">
        <p className="text-[12px] text-[#64748B] mb-3 max-w-[900px]">
          Mock games are real games to the app: fans following either team see them. Use test teams
          where you can, and delete games when you are done.
        </p>
        {refused && (
          <p
            role="alert"
            className="rounded-lg px-3 py-2 mb-3 text-[13px] bg-[#FEF2F2] text-[#B91C1C] max-w-[900px]"
          >
            {refused}
          </p>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-4 items-start">
          <div className="flex flex-col gap-4">
            <CreateGamePanel onCreated={(id) => select(id)} />
            <GameList
              games={games}
              selectedId={selectedId}
              onSelect={select}
              onDeleteAll={() => askDelete(null)}
              loading={loadingGames}
            />
          </div>

          <div className="flex flex-col gap-4 min-w-0">
            {notice && (
              <p
                role="status"
                className={`rounded-lg px-3 py-2 text-[13px] ${
                  notice.tone === 'error'
                    ? 'bg-[#FEE2E2] text-[#991B1B]'
                    : notice.tone === 'warn'
                      ? 'bg-[#FEF3C7] text-[#92400E]'
                      : 'bg-[#DCFCE7] text-[#166534]'
                }`}
              >
                {notice.text}
              </p>
            )}
            {game ? (
              <>
                <GameControls
                  game={game}
                  serverNow={state?.serverNow || serverNow}
                  busy={busy}
                  onUpdate={update}
                  onDelete={() => askDelete(game._id)}
                />
                <GameDayFeatures
                  game={game}
                  state={state}
                  fanwave={fanwave}
                  act={act}
                  busy={busy}
                />
              </>
            ) : (
              <Card>
                <Empty>
                  {loadingGames
                    ? 'Loading…'
                    : selectedId
                      ? 'That mock game no longer exists. Pick another from the list.'
                      : 'Create a mock game to start testing game day.'}
                </Empty>
              </Card>
            )}
          </div>
        </div>
      </div>

      {confirm && (
        <Alert
          text={confirm.text}
          accept={confirm.accept}
          refuse={() => setConfirm(null)}
          acceptText="Delete"
          refuseText="Cancel"
        />
      )}
    </div>
  );
};

export default GameDayLab;
